<script lang="ts">
	import { devices_status } from 'src/stores/device-runtime/status';
	//Hooks
	import { beforeUpdate } from 'svelte';
	import { format } from 'svelte-i18n';
	//Helpers
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		get_mode,
		is_gen_2,
		is_gen_z,
		real_id,
		real_id_z,
	} from '../../../helpers/interface_helpers';
	import { getStatus } from '../../../helpers/get_status_settings_helpers';
	import {
		localGroupRequest,
		handleCloudLocalLists,
		getDevicesFormatted,
		cloudGroupRequestV2,
	} from './control_group_helpers';

	//Stores
	import {
		createMemberSettingsSnapshot,
		createMemberStatusSnapshot,
		readDeviceStatus,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { isUsingGroupSlider } from 'src/stores/session/preferences';
	import { cloud_disabled_devices } from 'src/stores/device-runtime/firmware-ui';

	//Design system components
	import {
		ActionsContainer,
		DeviceButton,
		ControlsContainer,
		toast,
		Alert,
		DeviceButtonV2,
	} from '@Shelly/shelly-design-system';

	//Types
	import type { Device } from '../../../types/Device';
	import type { Group } from '../../../types/Group';
	import { loadingData } from 'src/stores/ui/loading-data';
	import { update_settings } from 'src/helpers/cloud_disabled_helpers';
	import { get_local_status } from 'src/services/local_requests';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { hasGroupControlRights } from 'src/helpers/user_helpers';
	interface _Group {
		id: string;
		mode: string;
		channel?: number;
		type?: string | number;
		gen?: number;
		category?: string;
	}

	// export let drawer: boolean = false;
	export let variant: 'listMenuCard' | 'widgetCard' | 'drawer' | 'dashControlWidget';
	export let group: Group;

	$: memberStatusStore = createMemberStatusSnapshot(group?.devices ?? []);
	$: memberSettingsStore = createMemberSettingsSnapshot(group?.devices ?? []);
	$: memberStatus = $memberStatusStore;
	$: memberSettings = $memberSettingsStore;

	let cloud = {} as Device;
	let local = {} as Device;
	let mutualRollersState: 'opening' | 'closing' | 'stop' = 'stop';

	let isLoading = {
		open: mutualRollersState == 'opening',
		close: mutualRollersState == 'closing',
	};

	$: disabled =
		!hasGroupControlRights(group, $devices) ||
		group.devices.every((deviceId) => {
			const status = memberStatus?.[real_id(deviceId)];
			return !status?.online;
		});

	let iconDown = 'fas fa-arrow-down';
	let iconUp = 'fas fa-arrow-up';
	let moving = false;
	// $: {
	// 	iconDown = isLoading.close ? 'fas fa-pause' : 'fas fa-arrow-down';
	// 	iconUp = isLoading.open ? 'fas fa-pause' : 'fas fa-arrow-up';
	// }
	beforeUpdate(() => {
		let group_devices: _Group[] = Object.values($devices)
			.filter(
				(d: Device): boolean =>
					group?.devices?.includes(d.id) &&
					typeof memberStatus[real_id(d.id)] !== 'undefined' &&
					memberSettings[real_id(d.id)] &&
					$devices[d.id].category == 'roller',
			)
			.map((d: { id }) => {
				return {
					mode: get_mode(d, memberStatus[real_id(d.id)], memberSettings[real_id(d.id)]),
					...d,
				};
			});

		for (const device of group_devices) {
			if (memberStatus[real_id(device.id)]?.online) {
				if (is_gen_2($devices[device.id])) {
					mutualRollersState = ['stopped', 'closed', 'open'].includes(
						memberStatus[real_id(device.id)]?.[
							`${get_gen_2_component_channel_structure(device)}`
						]?.state,
					)
						? 'stop'
						: memberStatus[real_id(device.id)]?.[
								`${get_gen_2_component_channel_structure(device)}`
							]?.state;
				} else if (is_gen_z($devices[device.id])) {
					moving =
						memberStatus[real_id(device.id)]?.['Meter:1'][
							'value.Electric_W_Consumed/66049'
						] !== 0;
					if (moving) {
						mutualRollersState = mutualRollersState;
					} else {
						setTimeout(() => {
							if (!moving) {
								mutualRollersState = 'stop';
								loadingData[mutualRollersState] = false;
							}
						}, 10000); //needed for faking direction control TODO: remove after firmware update with good statuses
					}
				} else {
					/**
					 * mapping gen 1 to gen 2 statuses, it's easier :)
					 */
					mutualRollersState =
						memberStatus[real_id(device.id)]?.rollers?.[device.channel].state === 'open'
							? 'opening'
							: memberStatus[real_id(device.id)]?.rollers?.[device.channel].state ===
								  'close'
								? 'closing'
								: 'stop';
				}
			}
		}

		isLoading = {
			open: mutualRollersState === 'opening',
			close: mutualRollersState === 'closing',
		};
		iconDown = isLoading.close ? 'fas fa-pause' : 'fas fa-arrow-down';
		iconUp = isLoading.open ? 'fas fa-pause' : 'fas fa-arrow-up';
		const lists = handleCloudLocalLists(group.devices);
		[cloud, local] = [lists.cloud, lists.local];
	});

	const sendRequest = async (control) => {
		isUsingGroupSlider.set(false);
		//if opening is incoming state - we send command OPEN (only in cases when the mutual roller state id different - Closing or Stop)
		// same for closing
		// if mutual roller state is same as incoming control we change it to stop, otherwise we override it with imcoming control
		const isGenZ = Object.values(cloud).some((d) => is_gen_z($devices[real_id_z(d.id)]));

		const _control: 'open' | 'close' | 'stop' =
			mutualRollersState === control ? 'stop' : control === 'opening' ? 'open' : 'close';
		mutualRollersState = isGenZ ? control : mutualRollersState === control ? 'stop' : control;
		isLoading = {
			open: mutualRollersState === 'opening',
			close: mutualRollersState === 'closing',
		};
		if (Object.keys(cloud).length > 0) {
			const data = { position: _control };
			let tmpDevices = getDevicesFormatted(Object.values(cloud), data);

			let res = await cloudGroupRequestV2(
				`/v2/devices/set/groups`,
				tmpDevices,
				data,
				isGenZ ? 12000 : undefined,
				isGenZ ? false : true,
			);

			if (
				(typeof res !== 'undefined' && Object.keys(res?.failedCommands || {}).length) ||
				!moving
			) {
				setTimeout(() => {
					loadingData[mutualRollersState] = false;
					mutualRollersState = 'stop';
				}, 5000); //needed for faking direction control TODO: remove after firmware update with good statuses
			}
		}

		if (Object.keys(local).length > 0) {
			for (const device of Object.values(local)) {
				let res;
				// if the device is cloud disabled we ask for status more frequently
				if ($cloud_disabled_devices[device.id]) {
					mutualRollersState = mutualRollersState;
					setTimeout(async () => {
						await getStatus(device);
					}, 1000);
				}
				if (is_gen_2(device)) {
					res = await localGroupRequest(
						device,
						{
							id: device.channel,
						},
						`${get_gen_2_component_from_category($devices[device.id])}.${_control}`,
					);
				} else {
					res = await localGroupRequest(device, { go: _control }, 'post', group);
				}
				if (res.isok) {
					const status_req = await get_local_status(
						$devices[device.id],
						$devices[device.id].ip,
					);
					let newStatus = {
						...memberStatus[real_id(device.id)],
						...status_req,
					};
					devices_status.update_device_status({
						id: real_id(device.id),
						msg: newStatus,
					});
					await update_settings($devices[device.id]);
				} else {
					toast.open({ type: 'danger', title: $format('error_global') });
				}
			}
		}
	};

	$: heading =
		group.devices.length === 0
			? $format('alert_heading_group_control_no_device', {
					default: 'Controls are unavailable. No devices are in the group.',
				})
			: $format('alert_heading_group_control_offline_removed_device', {
					default:
						'Controls are unavailable. All devices are currently offline or have been removed..',
				});
</script>

{#if variant === 'drawer'}
	{#if disabled}
		<Alert type="warning" class="mb-s" {heading} />
	{/if}
	<ControlsContainer color={$showNewLayout ? 'color-0' : undefined}>
		<DeviceButton
			aria-label={$format('open', { default: 'Open' })}
			id="devicebutton-fve1olu"
			slot="first"
			icon={iconUp}
			isActive={isLoading.open}
			isLoading={isLoading.open}
			size="l"
			on:click={async (event) => {
				event.stopPropagation();
				await sendRequest('opening');
			}}
			{disabled}
		/>

		<DeviceButton
			aria-label={$format('close', { default: 'Close' })}
			id="devicebutton-i3nny5w"
			slot="second"
			icon={iconDown}
			isActive={isLoading.close}
			isLoading={isLoading.close}
			size="l"
			on:click={async (event) => {
				event.stopPropagation();
				await sendRequest('closing');
			}}
			{disabled}
		/>
	</ControlsContainer>
{:else if variant === 'dashControlWidget'}
	<div class="d-flex gap-s">
		<DeviceButtonV2
			size="s"
			aria-label={$format('open', { default: 'Open' })}
			id="devicebutton-sbox5od"
			icon={iconUp}
			isActive={isLoading.open}
			isLoading={isLoading.open}
			variant="primary"
			on:click={async (event) => {
				event.stopPropagation();
				await sendRequest('opening');
			}}
			{disabled}
		/>

		<DeviceButtonV2
			size="s"
			aria-label={$format('close', { default: 'Close' })}
			id="devicebutton-7x1j561"
			variant="primary"
			icon={iconDown}
			isActive={isLoading.close}
			isLoading={isLoading.close}
			on:click={async (event) => {
				event.stopPropagation();
				await sendRequest('closing');
			}}
			{disabled}
		/>
	</div>
{:else if variant === 'listMenuCard' && $showNewLayout}
	<ActionsContainer
		orientation="vertical"
		slot="right"
		bgColor="color-base"
		type="separator"
		separatorColor="color-2"
	>
		<DeviceButtonV2
			aria-label={$format('open', { default: 'Open' })}
			id="devicebutton-sbox5od"
			slot="first"
			icon={iconUp}
			isActive={isLoading.open}
			isLoading={isLoading.open}
			on:click={async (event) => {
				event.stopPropagation();
				await sendRequest('opening');
			}}
			{disabled}
		/>
		<DeviceButtonV2
			aria-label={$format('close', { default: 'Close' })}
			id="devicebutton-7x1j561"
			slot="second"
			icon={iconDown}
			isActive={isLoading.close}
			isLoading={isLoading.close}
			on:click={async (event) => {
				event.stopPropagation();
				await sendRequest('closing');
			}}
			{disabled}
		/>
	</ActionsContainer>
{:else}
	<ActionsContainer
		type={variant === 'widgetCard' ? 'simple' : 'separator'}
		orientation={variant === 'widgetCard' ? 'horizontal' : 'vertical'}
	>
		<DeviceButton
			aria-label={$format('open', { default: 'Open' })}
			id="devicebutton-sbox5od"
			slot="first"
			icon={iconUp}
			isActive={isLoading.open}
			isLoading={isLoading.open}
			on:click={async (event) => {
				event.stopPropagation();
				await sendRequest('opening');
			}}
			{disabled}
		/>
		<DeviceButton
			aria-label={$format('close', { default: 'Close' })}
			id="devicebutton-7x1j561"
			slot="second"
			icon={iconDown}
			isActive={isLoading.close}
			isLoading={isLoading.close}
			on:click={async (event) => {
				event.stopPropagation();
				await sendRequest('closing');
			}}
			{disabled}
		/>
	</ActionsContainer>
{/if}
